import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIONS } from '../../constants/actionTables';
import dayjs from 'dayjs';
import { formatDateForReport } from '../../helpers/formatDate';
import { requestSearch } from '../../helpers/search';
import ModalMaintenanceTask from '../../components/ModalMaintenanceTask';
import TablePaginationActions from '../../components/TablePaginationActions';
import IntervalIndicator from '../../components/IntervalIndicator';

import { InputBase, Box, useMediaQuery, Button } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableHead } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import styles from './style.module.css';

import { useDatePickerLocalValue } from '../../hooks';
import 'dayjs/locale/de';

function CalendarList({ taskData, setStartDate, setEndDate, fetchData, startDate, endDate }) {
  const [rowsData, setRowsData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:820px)');

  useEffect(() => {
    if (rowsData.length <= rowsPerPage) {
      setPage(0);
    }
  }, [rowsData, rowsPerPage]);

  useEffect(() => {
    const sortedTaskData = [...taskData].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      return dateA - dateB;
    });
    setRowsData(sortedTaskData);
  }, [taskData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const titles = [
    t('generic.number'),
    t('generic.name'),
    t('generic.task'),
    t('generic.maintenanceType'),
    t('generic.status'),
    t('generic.scheduledDate'),
    t('generic.action'),
  ];

  const { value: localStartDate, setValue: setLocalStartDate } = useDatePickerLocalValue(
    startDate,
    setStartDate
  );

  const { value: localEndDate, setValue: setLocalEndDate } = useDatePickerLocalValue(
    endDate,
    setEndDate
  );

  const handleStartDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ');
    setStartDate(formattedDate);
    setLocalStartDate(date);
  };

  const handleEndDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ');
    setEndDate(formattedDate);
    setLocalEndDate(date);
  };

  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const searchFields = ['machineNumber', 'machineName', 'status', 'title'];
    requestSearch(searchValue, taskData, setRowsData, searchFields);
  }, [searchValue, taskData, setRowsData]);

  const handleResetFilters = () => {
    setSearchValue('');
    setStartDate(null);
    setEndDate(null);
    setLocalStartDate(null);
    setLocalEndDate(null);
  };

  const hasActiveFilters = searchValue || startDate || endDate;

  const tableContainerRef = useRef(null);
  useEffect(() => {
    tableContainerRef.current?.scrollIntoView();
  }, [page]);

  const renderMobileCard = (row, rowIndex) => {
    const cardData = {
      [t('generic.number')]: row.machineNumber,
      [t('generic.name')]: row.machineName,
      [t('generic.task')]:
        row.maintenanceName === 'Report a repair' ? t('modals.reportRepair') : row.maintenanceName,
      [t('generic.maintenanceType')]:
        row.maintenanceType === 'Report a repair' ? t('modals.reportRepair') : row.maintenanceType,
      [t('generic.status')]: t(`calendar.${row.status}`),
      [t('generic.scheduledDate')]: formatDateForReport(row.createdAt, 'date'),
    };

    return (
      <Paper
        key={row.id}
        sx={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '12px',
          border: '1px solid var(--table-border)',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'var(--table-hover)',
            transition: 'background-color 0.2s ease',
          },
        }}
      >
        {Object.entries(cardData).map(([key, value], index) => {
          const isLastItem = index === Object.entries(cardData).length - 1;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: isLastItem ? '12px' : '8px',
                fontSize: '0.875rem',
                borderBottom: isLastItem ? 'none' : '1px solid #eee',
                paddingBottom: isLastItem ? '0px' : '9px',
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  minWidth: '180px',
                  width: '180px',
                  flexShrink: 0,
                }}
              >
                {key}:
              </span>
              <span
                style={{
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  flex: 1,
                  wordBreak: 'break-word',
                }}
              >
                {value}
              </span>
            </div>
          );
        })}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexDirection: 'row-reverse',
            gap: '8px',
          }}
        >
          <ModalMaintenanceTask
            task={row}
            view={false}
            type={ACTIONS.CALENDAR}
            fetchdata={fetchData}
          />
          <IntervalIndicator
            intervalType={row.intervalType}
            intervalValue={row.intervalValue}
            size="medium"
            position="relative"
          />
        </div>
      </Paper>
    );
  };

  return (
    <div>
      <div className={styles.header}>
        <div
          className={styles.options}
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
            gap: '16px',
            marginBottom: '25px',
          }}
        >
          <Paper
            component="form"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: 300,
              border: '1px solid var(--table-border)',
              borderRadius: '12px',
              backgroundColor: 'var(--table-row-white)',
              boxShadow: 'none',
              '&:hover': {
                border: '1px solid var(--table-border)',
              },
              '&:focus-within': {
                border: '1px solid #3b82f6',
                boxShadow: '0 0 0 3px rgb(59 130 246 / 0.1)',
              },
              '@media (max-width: 675px)': {
                width: '100%',
              },
            }}
          >
            <IconButton
              type="button"
              sx={{
                p: '12px',
                color: 'var(--text-secondary)',
              }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{
                ml: 1,
                flex: 1,
                fontSize: '0.875rem',
                '& input': {
                  padding: '12px 16px 12px 0',
                  color: 'var(--text-primary)',
                },
              }}
              placeholder={t('generic.search')}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              value={searchValue}
            />
          </Paper>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid var(--table-border)',
              borderRadius: '12px',
              backgroundColor: 'var(--table-row-white)',
              boxShadow: 'none',
              height: '50px',
              minHeight: '50px',
              overflow: 'visible',
              '&:hover': {
                borderColor: 'var(--table-border)',
              },
              '&:focus-within': {
                borderColor: '#3b82f6',
                boxShadow: '0 0 0 3px rgb(59 130 246 / 0.1)',
              },
              '@media (max-width: 675px)': {
                width: '100%',
              },
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label={t('generic.from')}
                onChange={handleStartDateChange}
                value={localStartDate}
                sx={{
                  width: '50%',
                  '@media (min-width: 676px)': {
                    width: '140px',
                  },
                  '& .MuiInputBase-root': {
                    width: '100%',
                    height: '50px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:hover': {
                      border: 'none',
                    },
                    '&.Mui-focused': {
                      border: 'none',
                      boxShadow: 'none',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 8px',
                    fontSize: '0.875rem',
                    color: 'var(--text-primary)',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.75rem',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    '&.Mui-focused, &.MuiFormLabel-filled': {
                      transform: 'translateY(-26px) scale(0.75)',
                      transformOrigin: 'left top',
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <div
              style={{
                width: '1px',
                height: '24px',
                backgroundColor: 'var(--table-border)',
                margin: '0 8px',
              }}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label={t('generic.to')}
                onChange={handleEndDateChange}
                value={localEndDate}
                sx={{
                  width: '50%',
                  '@media (min-width: 676px)': {
                    width: '140px',
                  },
                  '& .MuiInputBase-root': {
                    width: '100%',
                    height: '50px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:hover': {
                      border: 'none',
                    },
                    '&.Mui-focused': {
                      border: 'none',
                      boxShadow: 'none',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 8px',
                    fontSize: '0.875rem',
                    color: 'var(--text-primary)',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.75rem',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    '&.Mui-focused, &.MuiFormLabel-filled': {
                      transform: 'translateY(-26px) scale(0.75)',
                      transformOrigin: 'left top',
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          {hasActiveFilters && (
            <Button
              variant="text"
              onClick={handleResetFilters}
              sx={{
                color: 'var(--primary-color)',
                fontSize: '0.875rem',
                textDecoration: 'underline',
                textTransform: 'none',
                padding: '8px 0',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {t('generic.resetFilters')}
            </Button>
          )}
        </div>
      </div>
      <div style={{ width: '100%' }} ref={tableContainerRef}>
        {/* Mobile View - Cards */}
        {isMobile ? (
          <div style={{ width: '100%' }}>
            {taskData && taskData.length > 0 ? (
              <>
                {(rowsPerPage > 0
                  ? rowsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : rowsData
                ).map((row, rowIndex) => renderMobileCard(row, rowIndex))}
              </>
            ) : (
              <Paper
                sx={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontStyle: 'italic',
                  borderRadius: '12px',
                  border: '1px solid var(--table-border)',
                  boxShadow: 'none',
                }}
              >
                NO DATA
              </Paper>
            )}
          </div>
        ) : /* Desktop View - Table */
        taskData.length ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              borderRadius: '12px 12px 0 0',
              border: '1px solid var(--table-border)',
              borderBottom: 'none',
              overflowX: 'auto',
              overflowY: 'hidden',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'var(--table-hover)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--text-secondary)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'var(--text-primary)',
                },
              },
            }}
          >
            <Table
              sx={{
                minWidth: 650,
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                  borderBottom: 'none !important',
                },
              }}
              aria-label="calendar list table"
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: 'var(--table-hover)',
                    '& .MuiTableCell-head': {
                      borderBottom: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--table-header-text)',
                      padding: '16px 20px',
                    },
                  }}
                >
                  {titles.map((title, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        width:
                          index === 0
                            ? '10%' // Number
                            : index === 1
                            ? '15%' // Name
                            : index === 2
                            ? '20%' // Task
                            : index === 3
                            ? '15%' // Maintenance Type
                            : index === 4
                            ? '12%' // Status
                            : index === 5
                            ? '15%' // Scheduled Date
                            : '10%', // Action
                      }}
                    >
                      {title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {rowsData && (
                <TableBody>
                  {(rowsPerPage > 0
                    ? rowsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : rowsData
                  ).map((row, rowIndex) => {
                    const isLastRow =
                      rowIndex ===
                      (rowsPerPage > 0
                        ? Math.min(rowsData.length, rowsPerPage) - 1
                        : rowsData.length - 1);

                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          borderBottom: isLastRow ? 'none' : '1px solid #eee',
                          '&:hover': {
                            backgroundColor: 'var(--table-hover)',
                            transition: 'background-color 0.2s ease',
                          },
                          '& .MuiTableCell-body': {
                            borderBottom: 'none',
                            color: 'var(--text-primary)',
                            fontSize: '0.875rem',
                            padding: '16px 20px',
                          },
                        }}
                      >
                        <TableCell align="left">{row.machineNumber}</TableCell>
                        <TableCell align="left">{row.machineName}</TableCell>
                        <TableCell align="left">
                          {row.maintenanceName === 'Report a repair'
                            ? t('modals.reportRepair')
                            : row.maintenanceName}
                        </TableCell>
                        <TableCell align="left">
                          {row.maintenanceType === 'Report a repair'
                            ? t('modals.reportRepair')
                            : row.maintenanceType}
                        </TableCell>

                        <TableCell align="left">{t(`calendar.${row.status}`)}</TableCell>
                        <TableCell align="left">
                          {formatDateForReport(row.createdAt, 'date')}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ padding: '0 !important', position: 'relative' }}
                        >
                          <div
                            style={{
                              padding: '16px 20px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                            }}
                          >
                            <ModalMaintenanceTask
                              task={row}
                              view={false}
                              type={ACTIONS.CALENDAR}
                              fetchdata={fetchData}
                            />
                            <IntervalIndicator
                              intervalType={row.intervalType}
                              intervalValue={row.intervalValue}
                              size="medium"
                              position="relative"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        ) : (
          <Paper
            sx={{
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--text-secondary)',
              fontStyle: 'italic',
              borderRadius: '12px',
              border: '1px solid var(--table-border)',
              boxShadow: 'none',
            }}
          >
            NO DATA
          </Paper>
        )}
      </div>

      {taskData.length > 0 && rowsData && (
        <Paper
          sx={{
            borderRadius: isMobile ? '12px' : '0 0 12px 12px',
            border: '1px solid var(--table-border)',
            boxShadow: 'none',
            marginBottom: '20px',
            marginTop: isMobile ? '16px' : '0',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 25, { label: 'All', value: -1 }]}
            component="div"
            count={rowsData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            labelRowsPerPage={t('calendar.rowsPerPage')}
            sx={{
              '& .MuiToolbar-root': {
                paddingRight: '20px',
                paddingLeft: '20px',
                minHeight: '64px',
              },
              '& .MuiTablePagination-selectLabel': {
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                '@media (max-width: 820px)': {
                  marginLeft: '10px',
                },
              },
              '& .MuiTablePagination-input': {
                '@media (max-width: 820px)': {
                  marginRight: '8px',
                },
              },
              '& .MuiTablePagination-actions': {
                '@media (max-width: 820px)': {
                  marginLeft: '10px',
                },
              },
            }}
            slotProps={{
              select: {
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              },
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </Paper>
      )}
    </div>
  );
}

export default CalendarList;
